import {
  Badge,
  Box,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { useCompanyCompareStore } from '../hooks/useCompanyCompareStore';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import { CompanyCompareMetrics } from './CompanyCompareMetrics';
import { CompanyComparePostings } from './CompanyComparePostings';
import { CompanyCompareSentiment } from './CompanyCompareSentiment';
import { useCompanyCompareData } from './useCompanyCompareData';

interface CompanyCompareDrawerProps {
  userSalary: number;
  userRoleLevel: RoleLevel | null;
  userGeoId: number | null;
  userGeoType: string | null;
  userPercentile: number | null;
}

export const CompanyCompareDrawer = ({
  userSalary,
  userRoleLevel,
  userGeoId,
  userGeoType,
  userPercentile,
}: CompanyCompareDrawerProps) => {
  const isDrawerOpen = useCompanyCompareStore((s) => s.isDrawerOpen);
  const closeDrawer = useCompanyCompareStore((s) => s.closeDrawer);
  const currentEmployer = useCompanyCompareStore((s) => s.currentEmployer);
  const selectedCompanies = useCompanyCompareStore((s) => s.selectedCompanies);
  const removeCompany = useCompanyCompareStore((s) => s.removeCompany);

  const allCompanies = useMemo(
    () =>
      currentEmployer
        ? [currentEmployer, ...selectedCompanies]
        : selectedCompanies,
    [currentEmployer, selectedCompanies]
  );

  const rcids = useMemo(() => allCompanies.map((c) => c.rcid), [allCompanies]);

  const {
    metrics,
    sentiments,
    postings,
    healthData,
    entityInfo,
    percentileSalaries,
    isLoading,
  } = useCompanyCompareData(
    rcids,
    userRoleLevel,
    userGeoId,
    userGeoType,
    isDrawerOpen,
    userPercentile
  );

  const entityInfoMap = useMemo(
    () => new Map(entityInfo.map((e) => [e.companyId, e])),
    [entityInfo]
  );

  const colCount = allCompanies.length;

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={closeDrawer}
      placement="right"
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent maxW="680px">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          <Text fontSize="md" fontWeight="bold" color="gray.800">
            Compare Companies
          </Text>
        </DrawerHeader>

        <DrawerBody px={4} py={4}>
          {isLoading && metrics.length === 0 ? (
            <VStack spacing={4} align="stretch">
              <Skeleton height="60px" />
              <Skeleton height="200px" />
              <Skeleton height="200px" />
              <Skeleton height="120px" />
            </VStack>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Company Headers */}
              <Grid templateColumns={`140px repeat(${colCount}, 1fr)`} gap={2}>
                <Box />
                {allCompanies.map((company) => {
                  const info = entityInfoMap.get(company.rcid);
                  const isPinned = currentEmployer?.rcid === company.rcid;
                  return (
                    <VStack key={company.rcid} spacing={1} align="center">
                      <HStack spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="gray.800"
                          textAlign="center"
                        >
                          {company.name}
                        </Text>
                        {isPinned ? (
                          <Badge colorScheme="green" fontSize="2xs">
                            You
                          </Badge>
                        ) : (
                          <CloseButton
                            size="sm"
                            onClick={() => removeCompany(company.rcid)}
                          />
                        )}
                      </HStack>
                      {info?.headquarters && (
                        <Text
                          fontSize="2xs"
                          color="gray.400"
                          textAlign="center"
                        >
                          {info.headquarters}
                        </Text>
                      )}
                      {info?.description && (
                        <Text
                          fontSize="2xs"
                          color="gray.400"
                          textAlign="center"
                          noOfLines={2}
                        >
                          {info.description}
                        </Text>
                      )}
                    </VStack>
                  );
                })}
              </Grid>

              {/* Key Metrics */}
              <CompanyCompareMetrics
                metrics={metrics}
                healthData={healthData}
                companyOrder={rcids}
                userSalary={userSalary}
                percentileSalaries={percentileSalaries}
                userPercentile={userPercentile}
              />

              {/* Sentiment */}
              <CompanyCompareSentiment
                sentiments={sentiments}
                companyOrder={rcids}
                isLoading={isLoading}
              />

              {/* Postings */}
              <CompanyComparePostings
                postings={postings}
                companyOrder={rcids}
                isLoading={isLoading}
              />
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
